import { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react'
import { createPortal } from 'react-dom'
import { components, MenuProps } from 'react-select'

export const useMenuCloseControl = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrollableAncestor, setScrollableAncestor] = useState<HTMLElement | null>(null)
  const controlRef = useRef<HTMLDivElement>(null)

  // Detect scrollable ancestor (modal container) when menu opens
  useEffect(() => {
    if (isMenuOpen && controlRef.current) {
      const ancestor = findScrollableAncestor(controlRef.current)
      setScrollableAncestor(ancestor)
    }
  }, [isMenuOpen])

  // Close dropdown when control element scrolls out of view
  useEffect(() => {
    if (!isMenuOpen || !controlRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries
        // Close dropdown if control is not intersecting (scrolled out of view)
        if (!entry.isIntersecting) {
          setIsMenuOpen(false)
        }
      },
      {
        root: scrollableAncestor, // Use modal container as root if available
        threshold: 0,
      }
    )

    observer.observe(controlRef.current)

    return () => {
      observer.disconnect()
    }
  }, [isMenuOpen, scrollableAncestor])

  return { isMenuOpen, setIsMenuOpen, controlRef }
}
/**
 * Find the closest scrollable ancestor element (like a modal container)
 */
export function findScrollableAncestor(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null

  let current = element.parentElement
  while (current) {
    const { overflow, overflowY } = window.getComputedStyle(current)
    const isScrollable = /(auto|scroll)/.test(overflow + overflowY)

    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current
    }

    current = current.parentElement
  }

  return null
}

const MenuContainer = styled.div<{ controlWidth?: number; isPositioned: boolean }>`
  z-index: 99999;
  position: absolute;
  width: ${({ controlWidth }) => (controlWidth ? `${controlWidth}px` : 'auto')};

  /* Hide menu until positioned to prevent flash at top-left corner */
  visibility: ${({ isPositioned }) => (isPositioned ? 'visible' : 'hidden')};
`

interface LegacyReactSelectMenuProps<T, IsMulti extends boolean = false> extends MenuProps<T, IsMulti> {
  controlRef?: React.RefObject<HTMLDivElement>
  flipMenu?: boolean
}

export const createLegacyMenuComponent = <T, IsMulti extends boolean = false>(
  controlRef: React.RefObject<HTMLDivElement>,
  options?: { flipMenu?: boolean }
) => {
  return (props: MenuProps<T, IsMulti>) => (
    <LegacyReactSelectMenu {...props} controlRef={controlRef} flipMenu={options?.flipMenu} />
  )
}

/**
 * NOTE: THIS COMPONENT IS ONLY USED FOR LEGACY DROPDOWN USAGE AND WILL BE REMOVED IN THE FUTURE.
 * Custom Menu component for react-select that uses floating-ui for smart positioning
 */
export function LegacyReactSelectMenu<T, IsMulti extends boolean = false>(
  props: LegacyReactSelectMenuProps<T, IsMulti>
) {
  const { children, innerProps, controlRef, flipMenu = true } = props
  const [boundary, setBoundary] = useState<HTMLElement | undefined>(undefined)
  const [isPositioned, setIsPositioned] = useState(false)

  const controlElement = controlRef?.current

  useEffect(() => {
    if (controlElement) {
      const scrollableAncestor = findScrollableAncestor(controlElement)
      setBoundary(scrollableAncestor || undefined)
    }
  }, [controlElement])

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      ...(flipMenu
        ? [
            flip({
              fallbackAxisSideDirection: 'start',
              padding: 8,
              boundary: boundary,
            }),
          ]
        : []),
      shift({
        padding: 8,
        boundary: boundary,
      }),
    ],
  })

  useEffect(() => {
    if (controlElement) {
      refs.setReference(controlElement)
    }
  }, [controlElement, refs])

  useEffect(() => {
    if (floatingStyles.position) {
      setIsPositioned(true)
    }
  }, [floatingStyles])

  const controlWidth = controlElement?.getBoundingClientRect().width

  return createPortal(
    <MenuContainer
      ref={refs.setFloating}
      style={floatingStyles}
      controlWidth={controlWidth}
      isPositioned={isPositioned}
    >
      {/* NOTE: Menu comes from react select */}
      <components.Menu {...props} innerProps={{ ...innerProps, style: { position: 'static', margin: 0 } }}>
        {children}
      </components.Menu>
    </MenuContainer>,
    document.body
  )
}
